import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import * as Sentry from "@sentry/react";
import Skeleton from "../library/Skeleton";
import SettingLabel from "./SettingLabel";
import { formatCurrentPasswordError } from "./utils";

const MobileNumberSettingOption = <User extends RecruiterUser | EmployerUser>(
  props: Props<User>
) => {
  const {
    user,
    isEditing = false,
    disabled = false,
    isLoading = false,
    onEdit,
    onDone,
    onUserChange,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    if (values.mobileNumber === user.mobileNumber) {
      onDone();
      return;
    }

    try {
      user.assignAttributes(values);
      const success = await user.save();
      if (success) {
        onUserChange?.(user);
        onDone();
      } else {
        formik.setErrors({
          mobileNumber: user.errors.mobileNumber?.fullMessage,
          currentPassword: formatCurrentPasswordError(
            user.errors.currentPassword
          ),
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      user.rollback();
    }
  };

  const edit = () => {
    if (!disabled) {
      onEdit();
    }
  };

  const showNotSpecified = !isLoading && !user?.mobileNumber;
  const showName = !isLoading && Boolean(user?.mobileNumber);

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Mobile number</SettingLabel>
          <TextLink disabled={disabled} onClick={edit}>
            Edit
          </TextLink>
        </Header>
        {isLoading && <Skeleton height="22" />}
        {showNotSpecified && <div>Not specified</div>}
        {showName && <div>{user.mobileNumber}</div>}
      </section>
    );
  }

  const initialValues: FormValues = {
    mobileNumber: user.mobileNumber,
    currentPassword: "",
  };

  const validation = Yup.object({
    mobileNumber: Yup.string()
      .matches(/^[ 0-9()-]{5,}$/, "Please enter a valid mobile number")
      .required("Please enter a mobile number"),
    currentPassword: Yup.string().required("Please enter your password"),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Mobile number</SettingLabel>
            <TextLink onClick={onDone}>Cancel</TextLink>
          </Header>
          <TextInputField
            label="Mobile number"
            hiddenLabel
            name="mobileNumber"
          />
          <TextInputField
            label="Confirm password"
            type="password"
            name="currentPassword"
          />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type Props<User> = {
  user: User;
  isEditing?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onEdit: () => void;
  onDone: () => void;
  onUserChange?: (user: User) => void;
};

type FormValues = {
  mobileNumber: string;
  currentPassword: string;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default MobileNumberSettingOption;
